import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, EditableSlotGroup } from '@vibesites/runtime/react';

// ============================================================================
// Slot group definitions (inline literals so the schema is statically extractable)
// ============================================================================

const cta1Def: SlotGroupDefinition = {
  label: 'Callout 1',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Find Your Space' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const cta2Def: SlotGroupDefinition = {
  label: 'Callout 2',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/virtual-tour' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Take a Virtual Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const cta3Def: SlotGroupDefinition = {
  label: 'Callout 3',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/amenities' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Explore Our Amenities' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const cta4Def: SlotGroupDefinition = {
  label: 'Callout 4',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/gallery' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Browse The Gallery' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Component
// ============================================================================

/* Base strip colors are hard-coded Homestead values; hover fills secondary
   (teal) for a clear contrast against the warm strip, with lift + shadow. */
const CALLOUT_LINK_CLASS =
  'hero-callout-link group flex items-center justify-center gap-2 py-6 px-4 text-center font-heading font-bold text-xs md:text-sm tracking-wide text-[#342E1F]';

/** Small right-pointing arrow shown after each callout label. */
function CalloutArrow() {
  return (
    <span
      aria-hidden="true"
      className="inline-block text-[0.7em] leading-none translate-y-px transition-transform duration-300 group-hover:translate-x-1"
    >
      ▸
    </span>
  );
}

export function HeroCallouts() {
  const cta1 = useSlotGroup('herocallouts.cta1', cta1Def);
  const cta2 = useSlotGroup('herocallouts.cta2', cta2Def);
  const cta3 = useSlotGroup('herocallouts.cta3', cta3Def);
  const cta4 = useSlotGroup('herocallouts.cta4', cta4Def);

  return (
    <section className="w-full bg-white px-3 sm:px-5 md:px-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-[#E2DDD5] bg-[#F5F3EF]">
        <EditableSlotGroup groupPath="herocallouts.cta1" definition={cta1Def}>
          <a href={cta1.url || '/floor-plans'} target={cta1._target || '_self'} className={CALLOUT_LINK_CLASS}>
            <span>{cta1.label || 'Find Your Space'}</span>
            <CalloutArrow />
          </a>
        </EditableSlotGroup>

        <EditableSlotGroup groupPath="herocallouts.cta2" definition={cta2Def}>
          <a href={cta2.url || '/virtual-tour'} target={cta2._target || '_self'} className={CALLOUT_LINK_CLASS}>
            <span>{cta2.label || 'Take a Virtual Tour'}</span>
            <CalloutArrow />
          </a>
        </EditableSlotGroup>

        <EditableSlotGroup groupPath="herocallouts.cta3" definition={cta3Def}>
          <a href={cta3.url || '/amenities'} target={cta3._target || '_self'} className={CALLOUT_LINK_CLASS}>
            <span>{cta3.label || 'Explore Our Amenities'}</span>
            <CalloutArrow />
          </a>
        </EditableSlotGroup>

        <EditableSlotGroup groupPath="herocallouts.cta4" definition={cta4Def}>
          <a href={cta4.url || '/gallery'} target={cta4._target || '_self'} className={CALLOUT_LINK_CLASS}>
            <span>{cta4.label || 'Browse The Gallery'}</span>
            <CalloutArrow />
          </a>
        </EditableSlotGroup>
      </div>

      <style>{`
        .hero-callout-link {
          transition: background-color 0.3s ease, color 0.3s ease, transform 0.3s ease, box-shadow 0.3s ease;
        }
        .hero-callout-link:hover {
          background: var(--vibe-color-secondary);
          color: var(--vibe-color-text-on-secondary, #fff);
          transform: translateY(-2px);
          box-shadow: 0 8px 22px color-mix(in srgb, var(--vibe-color-secondary) 35%, transparent);
          position: relative;
          z-index: 1;
        }
        .hero-callout-link:active {
          transform: translateY(0);
          box-shadow: none;
        }
        @media (prefers-reduced-motion: reduce) {
          .hero-callout-link:hover,
          .hero-callout-link:active { transform: none; box-shadow: none; }
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'HeroCallouts',
  displayName: 'Hero Callouts',
  tokens: {},
  slotGroups: {
    'herocallouts.cta1': cta1Def,
    'herocallouts.cta2': cta2Def,
    'herocallouts.cta3': cta3Def,
    'herocallouts.cta4': cta4Def,
  },
};

(HeroCallouts as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default HeroCallouts;
